/* eslint-disable no-unused-vars,no-bitwise */
/* global G, BigInt */
import CryptoJS from 'crypto-js'
import * as forge from 'node-forge'
import { DecryptionFailedError, OutdatedKeyError, parsePinContent } from 'platform/adapter/pin/helper'

/**
 * Generates the HOTP from a hex code as input
 *
 * @param {PIN.KEY} key     the secret key
 * @param {number} counter  the magic bit
 * @returns {string}
 */
const generateHOTPFromHex = (key, counter) => {
  const hexCounter = counter.toString(16).padStart(16, '0')
  const hmacDigest = CryptoJS.HmacSHA256(
    CryptoJS.enc.Hex.parse(hexCounter),
    CryptoJS.enc.Hex.parse(key),
  )

  // 2. Convert the CryptoJS WordArray to a raw byte sequence
  const data = hmacDigest.words
  const bytes = new Uint8Array(hmacDigest.sigBytes)

  // 3. Convert words (32-bit integers) to 8-bit bytes (Big-Endian conversion)
  for (let i = 0; i < hmacDigest.sigBytes; i++) {
    bytes[i] = (data[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xFF
  }

  // 4. Dynamic Truncation: Find the offset (low 4 bits of the last byte)
  const offset = bytes[bytes.length - 1] & 0x0F

  // 5. Extract 4 bytes starting at the offset and convert to a 32-bit integer
  let otpBinary = 0
  for (let i = 0; i < 4; i++) {
    otpBinary = (otpBinary << 8) | bytes[offset + i]
  }

  // 6. Mask off the sign bit (the highest bit must be ignored)
  otpBinary &= 0x7FFFFFFF

  // 7. Final Modulo and Formatting
  return (otpBinary % (10 ** 8)).toString().padStart(8, '0')
}

/**
 * Generates the HOTP from a QR code as input
 *
 * @param {PIN.KEY} key     the secret key
 * @param {number} counter  the magic bit
 * @returns {string}
 */
const generateHOTPFromQR = (key, counter) => {
  const counterHex = `0000000000000000${BigInt(counter).toString(16)}`.slice(-16)
  const counterWordArray = CryptoJS.enc.Hex.parse(counterHex)
  const keyWordArray = CryptoJS.enc.Utf8.parse(key)

  // Calculate HMAC using SHA-256
  const hmac = CryptoJS.HmacSHA256(counterWordArray, keyWordArray)
  const hmacHex = hmac.toString(CryptoJS.enc.Hex)

  // Dynamic truncation to get the OTP
  // Get the offset from the last nibble (half-byte) of the HMAC
  const offset = parseInt(hmacHex.substring(hmacHex.length - 1), 16) & 0xf

  // Get the 4 bytes from the HMAC starting at the offset
  const otpBinary = parseInt(hmacHex.substring(offset * 2, offset * 2 + 8), 16) & 0x7fffffff

  // Mod the binary with 10^6 to get a 6-digit OTP, pad with leading 0s if necessary
  return (otpBinary % (10 ** 8)).toString().padStart(8, '0')
}

/**
 * Creates a PIN from a QR code
 *
 * @param {Gaia.Web.Application} app  the Platform Web Application
 * @returns {PinEventListener}        an PinEventListener
 */
const fn = app => async ({ detail = {} }) => {
  const adapter = app[G.ADAPTER][G.PIN]
  const { privateKeys = [] } = adapter[G.STATE]
  const { code = null } = detail
  const hexCode = Number.parseInt(code, 16)
  const isBase64 = code.replaceAll(' ', '').length > 8

  let i
  let decrypted
  let hexKey

  if (code && isBase64) {
    for (i = 0; i < privateKeys.length; i++) {
      const { data: { pv: privateKey } = {} } = privateKeys[i]

      if (!privateKey) {
        throw new ReferenceError('Private key is not defined')
      }

      try {
        const parsedPrivateKey = forge.pki.privateKeyFromPem(privateKey)
        const encryptedBytes = forge.util.decode64(code)

        decrypted = parsedPrivateKey.decrypt(encryptedBytes, 'RSA-OAEP', {
          md: forge.md.sha256.create(),
          mgf1: {
            md: forge.md.sha256.create(),
          },
        })

        if (decrypted) break
      } catch (e) {
        console.error(e)
      }
    }
  } else if (code && !isBase64) {
    try {
      // Only use the first/newest key here, not all of them
      const { data: { pv: privateKey } = {} } = privateKeys[0]

      if (!privateKey) {
        throw new ReferenceError('Private key is not defined')
      }

      const parsedPrivateKey = forge.pki.privateKeyFromPem(privateKey)
      const rsaPrivateKeyAsn1 = forge.pki.privateKeyToAsn1(parsedPrivateKey)
      const privateKeyInfoAsn1 = forge.pki.wrapRsaPrivateKey(rsaPrivateKeyAsn1)
      const derBuffer = forge.asn1.toDer(privateKeyInfoAsn1)

      // derive symmetric secret
      const md = forge.md.sha256.create()
      md.update(derBuffer.getBytes())
      hexKey = forge.util.bytesToHex(md.digest().getBytes())
    } catch (e) {
      console.error(e)
    }
  }

  if (isBase64 && !decrypted) {
    return { error: DecryptionFailedError() }
  }

  const { key, magic, serial, software, request } = isBase64
    ? parsePinContent(decrypted)
    : { key: hexKey, magic: hexCode }

  const pin = isBase64
    ? generateHOTPFromQR(key, magic)
    : generateHOTPFromHex(hexKey, magic)

  const readablePin = `${pin.slice(0, 4)} ${pin.slice(-4)}`

  return {
    pin: readablePin,
    software,
    serial,
    request,
    error: isBase64 && i !== 0 ? OutdatedKeyError() : null,
  }
}

export default fn
